import { assertNever } from "@@/shared/assert-never";
import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import type { OrderChangedEvent } from "@@/shared/orders/order-events/events/order-changed-event/order-changed.event";
import { isOrderItemBookableTimeOrderChange } from "@@/shared/orders/order-events/events/order-changed-event/order-changes/order-item-bookable-time.order-change";
import type { OrderConfirmedEvent } from "@@/shared/orders/order-events/events/order-confirmed.event";
import type { OrderCreatedFromQuoteEvent } from "@@/shared/orders/order-events/events/order-created-from-quote.event";
import type { OrderCreatedEvent } from "@@/shared/orders/order-events/events/order-created.event";
import { isOrderItemBookable } from "@@/shared/orders/order-items/is-order-item";
import { isOrderItemAccommodation_V2 } from "@@/shared/orders/order-items/is-order-item-v2";
import type { OrderProjection } from "@@/shared/orders/order-projections/main/order.projection";
import type { Settlement } from "./settlements/settlement";

function parseFulfillmentDate(
    event: OrderCreatedEvent | OrderChangedEvent | OrderCreatedFromQuoteEvent,
): IsoAndUnixTimestamp;
function parseFulfillmentDate(
    event: OrderConfirmedEvent,
    currentFulfillmentDate: IsoAndUnixTimestamp,
): IsoAndUnixTimestamp;
function parseFulfillmentDate(
    event:
        | OrderCreatedEvent
        | OrderConfirmedEvent
        | OrderChangedEvent
        | OrderCreatedFromQuoteEvent,
    currentFulfillmentDate?: IsoAndUnixTimestamp,
): IsoAndUnixTimestamp {
    switch (event.type) {
        case "CREATED_FROM_QUOTE":
        case "CREATED": {
            // find bookable order items
            // and find out when the first of them starts
            const bookables = event.order.orderItems
                .filter(isOrderItemBookable)
                .sort((a, b) => a.time.start.unix - b.time.start.unix);

            if (bookables?.[0]) {
                // if there are bookables, return the first
                // ones start time, as the fulfillment date
                return bookables?.[0].time.start;
            }

            const accommodation = event.order.orderItems
                .filter(isOrderItemAccommodation_V2)
                .sort((a, b) => a.time.start.unix - b.time.start.unix);
            if (accommodation?.[0]) {
                // if there are accommodation, return the first
                // ones start time, as the fulfillment date
                return accommodation?.[0].time.start;
            }

            if (event.order.delivery.estimatedDeliveryTime) {
                // if there are no bookables, but there is an estimated delivery time
                // return that as the fulfillment date
                return event.order.delivery.estimatedDeliveryTime;
            }

            // if there are no bookables, and no estimated delivery time
            // return the order created time as the fulfillment date
            return event.order._createdAt;
        }
        case "CONFIRMED": {
            // if there is an estimated delivery time
            // return that as the fulfillment date else the
            // fulfillment date hasn't been changed by the confirmation
            if (event.estimatedDeliveryTime) {
                return event.estimatedDeliveryTime;
            }

            if (!currentFulfillmentDate) {
                throw new Error(
                    `currentFulfillmentDate is required for order confirmed event fulfillment date check; ${event._id}`,
                );
            }

            return currentFulfillmentDate;
        }
        case "ORDER_CHANGED": {
            return event.fulfillmentDate;
        }
        default:
            assertNever(event, "Unknown order event type", {
                valueProp: "type",
            });
    }
}

const parseSettlementFulfillmentDate = (settlement: Settlement) => {
    const originEvent = settlement.originEvent;
    if (originEvent.type === "CREATED") {
        const bookables =
            originEvent.order.orderItems.filter(isOrderItemBookable);
        if (bookables?.[0]) return bookables?.[0].time.start;

        if (originEvent.order.delivery.estimatedDeliveryTime)
            return originEvent.order.delivery.estimatedDeliveryTime;
        return originEvent.order._createdAt;
    }

    if (originEvent.type === "ORDER_CHANGED") {
        // Check if any of the changes are bookable time changed
        const fulfillmentDataChangedTo = originEvent.changes.filter(
            isOrderItemBookableTimeOrderChange,
        )?.[0].data.to;

        if (fulfillmentDataChangedTo) return fulfillmentDataChangedTo?.start;

        // Else use the orders original fulfillment date
        // TODO!! @emanuel @jakob
        throw new Error(
            "HAVE NOT IMPLEMENTED GETTING ORIGINAL ORDER CREATED FULFILLMENT DATE FOR CHANGE YET",
        );
    }

    assertNever(
        originEvent,
        "Unknown origin event type in settlement item factory",
        { valueProp: "type" },
    );
};
const parseOrderProjectionFulfillmentDate = (order: OrderProjection) => {
    // find bookable order items
    // and find out when the first of them starts
    const bookables = order.orderItems
        .filter(isOrderItemBookable)
        .sort((a, b) => a.time.start.unix - b.time.start.unix);

    if (bookables?.[0]) {
        // if there are bookables, return the first
        // ones start time, as the fulfillment date
        return bookables?.[0].time.start;
    }

    if (order.delivery.estimatedDeliveryTime) {
        // if there are no bookables, but there is an estimated delivery time
        // return that as the fulfillment date
        return order.delivery.estimatedDeliveryTime;
    }

    // if there are no bookables, and no estimated delivery time
    // return the order created time as the fulfillment date
    return order._createdAt;
};

export {
    parseFulfillmentDate,
    parseOrderProjectionFulfillmentDate,
    parseSettlementFulfillmentDate,
};
